import { useEffect, useState } from "react";
import { Loader2, Mail, MailOpen, Trash2, Reply, X, BookOpen, PlayCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function fmt(iso) {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return ""; }
}

export default function AdminMessages() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);

  const load = () => api.get("/admin/messages").then(({ data }) => {
    setData(data);
    window.dispatchEvent(new CustomEvent("efa:messages-unread-changed", { detail: { unread: data.unread } }));
  });
  useEffect(() => { load(); }, []);

  const setRead = async (m, read) => {
    await api.post(`/admin/messages/${m.id}/read`, { read });
    await load();
    setActive((a) => (a && a.id === m.id ? { ...a, read } : a));
  };
  const del = async (m) => {
    if (!confirm("Delete this message permanently?")) return;
    await api.delete(`/admin/messages/${m.id}`);
    if (active && active.id === m.id) setActive(null);
    toast.success("Message deleted");
    load();
  };
  const open = async (m) => {
    setActive(m);
    if (!m.read) await setRead(m, true);
  };

  if (!data) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;

  const list = data.items.filter((m) => {
    const s = q.toLowerCase();
    return !s || [m.name, m.email, m.subject, m.message, m.course, m.lesson].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Messages</h1>
          <p className="mt-1 text-sm text-zinc-400">Student questions from "Ask the Instructor".</p>
        </div>
        <div className="flex items-center gap-3 text-sm text-zinc-400">
          <span>Unread: <b className="text-[#DCA846]" data-testid="messages-unread-count">{data.unread}</b></span>
          <span>Total: <b className="text-white">{data.total}</b></span>
        </div>
      </div>

      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search messages..."
        className="mt-5 max-w-sm bg-zinc-900 border-white/10 text-white" data-testid="messages-search" />

      <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_1.2fr]">
        {/* LIST */}
        <div className="space-y-2" data-testid="messages-list">
          {list.length === 0 ? (
            <div className="rounded-lg border border-white/10 p-10 text-center text-zinc-500">
              <Mail className="mx-auto mb-2 h-6 w-6 text-zinc-600" /> No messages yet.
            </div>
          ) : list.map((m) => (
            <button key={m.id} data-testid={`message-row-${m.id}`} onClick={() => open(m)}
              className={`flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors ${
                active && active.id === m.id ? "border-[#DCA846]/60 bg-[#DCA846]/10" : "border-white/10 hover:border-white/20 hover:bg-white/5"
              } ${m.read ? "" : "bg-[#DCA846]/5"}`}>
              <div className="mt-0.5 shrink-0 text-[#DCA846]">{m.read ? <MailOpen className="h-4 w-4" /> : <Mail className="h-4 w-4" />}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className={`truncate ${m.read ? "text-zinc-300" : "font-semibold text-white"}`}>{m.subject}</span>
                  {!m.read && <span className="h-2 w-2 shrink-0 rounded-full bg-[#DCA846]" />}
                </div>
                <p className="truncate text-xs text-zinc-500">{m.name} · {m.email}</p>
                <p className="mt-1 truncate text-sm text-zinc-400">{m.message}</p>
                <p className="mt-1 text-xs text-zinc-600">{fmt(m.created_at)}</p>
              </div>
            </button>
          ))}
        </div>

        {/* DETAIL */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          {active ? (
            <div data-testid="message-detail" className="rounded-xl border border-white/10 bg-zinc-900/40 p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-display text-xl uppercase tracking-wide text-white break-words">{active.subject}</h2>
                  <p className="mt-1 text-sm text-zinc-400">{active.name} · <a href={`mailto:${active.email}`} className="text-[#DCA846] hover:underline">{active.email}</a></p>
                  <p className="text-xs text-zinc-600">{fmt(active.created_at)}</p>
                </div>
                <button onClick={() => setActive(null)} className="shrink-0 text-zinc-500 hover:text-white lg:hidden"><X className="h-5 w-5" /></button>
              </div>

              {(active.course || active.context) && (
                <div className="mt-4 flex flex-wrap gap-2 text-xs">
                  {(active.course || active.context) && <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/40 px-3 py-1 text-zinc-300"><BookOpen className="h-3 w-3 text-[#DCA846]" /> {active.course || active.context}</span>}
                  {active.lesson && <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/40 px-3 py-1 text-zinc-300"><PlayCircle className="h-3 w-3 text-[#DCA846]" /> {active.lesson}</span>}
                </div>
              )}

              <div className="mt-5 whitespace-pre-wrap rounded-lg bg-black/40 p-4 text-sm leading-relaxed text-zinc-200">{active.message}</div>

              <div className="mt-6 flex flex-wrap gap-2">
                <a href={`mailto:${active.email}?subject=${encodeURIComponent("Re: " + active.subject)}`} data-testid="message-reply-btn"
                  className="inline-flex items-center gap-2 rounded-sm bg-[#DCA846] px-4 py-2 text-sm font-display uppercase tracking-wide text-black hover:bg-[#C5963B]">
                  <Reply className="h-4 w-4" /> Reply
                </a>
                <Button variant="outline" data-testid="message-toggle-read-btn" onClick={() => setRead(active, !active.read)}
                  className="border-white/15 text-white">
                  {active.read ? <><Mail className="mr-1 h-4 w-4" /> Mark Unread</> : <><MailOpen className="mr-1 h-4 w-4" /> Mark Read</>}
                </Button>
                <Button variant="outline" data-testid="message-delete-btn" onClick={() => del(active)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="mr-1 h-4 w-4" /> Delete
                </Button>
              </div>
            </div>
          ) : (
            <div className="hidden rounded-xl border border-dashed border-white/10 p-16 text-center text-zinc-500 lg:block">
              Select a message to read it.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
